import Divider from "../components/divider";
import Hero from "../components/hero";
import SeoMeta from "../components/seo-meta";
import { getSEOPageProps } from "../lib/seo";

import styles from "./policy.module.scss";
import config from "../config";

export default function TermsPage({ seo }) {
  return (
    <div className="container">
      <SeoMeta {...seo} />
      <Hero title="East Room Cookie Policy" />

      <div className={styles.Policy}>
        <section className={styles.Intro}>
          <p>Last updated: March 28, 2022</p>

          <p>
            Safeguarding your privacy is important to East Room (operating as
            “East Room Holding Corp”). We, as well as certain third parties that
            provide content and other functionality on our websites and mobile
            applications (“Services”) use cookies, web beacons, pixels, and
            other similar tracking technologies on our online Services. This
            Cookie Policy (“Policy”) explains how we use such technologies to
            enable certain functionalities, as well as to analyze and improve
            your use of our Services. Some of these cookies may lead to us
            collecting personal information about you, and you can learn more
            about how we use that personal information in our East Room Privacy
            Policy.
          </p>
        </section>

        <Divider className={styles.Divider} />

        <section>
          <h2>1. What Technologies We Use</h2>

          <p>
            <strong> Cookies.</strong>
          </p>

          <p>
            A cookie is a small file stored on your computer or other device. A
            cookie enables the entity that put the cookie on your device to
            recognize it across different websites, services, devices, and
            browsing sessions. By storing such information, cookies can, among
            other things, remember your preferences and settings for the
            Services and helps us analyze how you use the Services. To see the
            categories of cookies that we use, please see Section 4 of this
            Policy.
          </p>

          <p>
            <strong>Web Beacons / Pixel Tags.</strong>
          </p>

          <p>
            Web beacons or pixel tags and similar technologies are small bits of
            code, which are embedded in web pages, ads, and e-mail, that
            communicate with third parties. Pixel tags can collect information
            such as the Internet Protocol (“IP”) address of the computer that
            downloaded the page on which the web beacon appears; the URL of the
            page on which the pixel tag appears; the time the page containing
            the pixel tag was viewed; the type of browser that fetched the pixel
            tag; and the identification number of any cookie on the computer
            previously placed by that server. We may use web beacons, for
            example, to count the number of users who have visited a particular
            web page, to deliver or communicate with cookies, and to understand
            usage patterns. We also may include web beacons in e-mails to
            understand whether messages have been opened, acted on, or
            forwarded.
          </p>

          <p>
            <strong>Other Technologies.</strong>
          </p>

          <ul>
            <li>
              There are other local storage and Internet technologies, such as
              Local Shared Objects (also referred to as “Flash cookies”) and
              HTML5 local storage that operate similarly to the cookies
              discussed above in that they are stored on your device and can be
              used to store certain information about your activities and
              preferences across different services and sessions.
            </li>
            <li>
              Please note that these technologies are distinct from cookies, and
              you may not be able to control them using standard browser tools
              and settings.
            </li>
          </ul>
        </section>

        <section>
          <h2>2. Why We Use These Technologies</h2>

          <p>
            Our websites use these technologies for the following general
            purposes:
          </p>
          <ul>
            <li>
              Administering and improving our Services, including helping us
              measure and research the effectiveness of our content, features,
              advertisements, and other communications. For example, we measure
              which pages and features website visitors are accessing and how
              much time they are spending on our webpages. We may include web
              beacons in e-mails, for example, to understand whether messages
              have been opened, acted on, or forwarded.
            </li>
            <li>
              Storing your sign-in credentials and preferences so that you don’t
              have to enter those credentials and preferences every time you log
              on to a Service.
            </li>
            <li>
              Helping us and third parties provide you with relevant content and
              advertising by collecting information about your use of our
              Services and other websites.
            </li>
          </ul>

          <p>
            Cookies are an important part of our Services, so you should be
            aware that if you choose to remove cookies, this could affect the
            availability and functionality of the Services.
          </p>

          <p>
            When you use a web browser to access the Services, many browsers
            allow you to configure your browser to accept all cookies, reject
            all cookies, or notify you when a cookie is sent. Click the “Help”
            menu of your browser to learn more about how to change your cookie
            preferences. The operating system of your device may contain
            additional controls for cookies. Please note that disabling cookies
            may affect your ability to access and use certain features of the
            Services. To learn more about cookies and how to manage them, please
            click here. To opt-out of internet-based advertising by all
            companies which participate in the self-regulatory Digital
            Advertising Alliance (“DAA”), please visit{" "}
            <a
              href="http://www.aboutads.info/choices/"
              target="_blank"
              rel="noreferrer"
            >
              http://www.aboutads.info/choices/
            </a>
            .{" "}
          </p>

          <p>
            To limit interest-based advertising on your mobile device, you can
            review and adjust the settings provided by your device manufacturer,
            such as “Limit Ad Tracking” for iOS or “Opt-out of interest-based
            ads” for Android.
          </p>
        </section>

        <section>
          <h2>3. The Types of Cookies We Use</h2>
          <p>
            In an effort to provide increased transparency with respect to our
            use of cookies, we wanted to provide you with additional information
            about the types of cookies we employ. We may use any of the
            following types of cookies on the Services as described in the table
            below:
          </p>

          <p>
            <b>Cookie Type - Description</b>
          </p>

          <ul>
            <li>
              <strong>Essential Cookies</strong>: These cookies are necessary
              for the functionality of the Services. These cookies can be
              disabled in your browser but may prevent certain parts of the
              Services from functioning.
            </li>
            <li>
              <strong>Analytics Cookies</strong>: These cookies help us to
              determine the source and amount of traffic to the Services and
              help us to better understand how our visitors use the Services. If
              you choose to disable these cookies, it will impact our ability to
              measure the performance of the Services.
            </li>
            <li>
              <strong>Functionality Cookies</strong>: These cookies help us to
              enhance the functionality of our website, including through the
              use of customization or personalization. These cookies may be set
              by third parties. Disabling these cookies may prevent certain
              parts of the Services from functioning.
            </li>
            <li>
              <strong>Advertising or Targeting Cookies</strong>: Advertising or
              targeting cookies may be set by third parties to facilitate the
              delivery of targeted advertising, including through the delivery
              of interest-based advertising, and helping us measure the
              effectiveness of our campaigns. If you disable these cookies, you
              may receive less relevant or targeted advertising.
            </li>
          </ul>
        </section>

        <section>
          <h2>4. Questions</h2>
          <p>
            If you have any questions or comments regarding our Policy, please
            contact us at:
            <br /> Privacy East Room Holding Corp. 9 Davies, Toronto, ON. M4M
            1G3 Email: info@eastroom.com
            <br /> East Room will address your concerns and attempt to resolve
            any privacy issues in a timely manner.
          </p>
        </section>
      </div>
    </div>
  );
}

export async function getStaticProps() {
  return {
    props: {
      seo: await getSEOPageProps({ resolvedUrl: "/cookie-policy" })
    },
    revalidate: config.ISR_TIMING
  };
}
